import React, { useEffect, useState } from 'react'
import {
  EmtButton,
  EmtCard,
  EmtFloatingIconButton,
  EmtIcon,
  EmtMap,
  EmtTypography,
  MarkerList,
} from 'emt-components-v2'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import { MapFinderCoordinates } from './Map'
import { Constants } from '@/assets'
import ProfileCardV2 from '@/components/EMT/ProfileCardV2'
import { useTranslation } from 'next-i18next'
import useFilters from '@/hooks/useFilters'
import FiltersButton from '@/components/search/FiltersButton'
import Backdrop from '@mui/material/Backdrop'
import { LinearProgress } from '@mui/material'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import useHorizontalSwipe from '@/hooks/useHorizontalSwipe'
import Skeleton from '@mui/material/Skeleton'
import { UserCard } from '@/dataV3/Search/UserCard'
import { SearchFiltersValues } from '@/modules/Search/apiTypes/base'
import FloatingProfileModal from '@/components/FloatingProfileModal'

type Props = {
  homeMarker: React.ReactNode
  markers: MarkerList
  coordinates: MapFinderCoordinates
  profiles: UserCard[]
  selectedProfile: UserCard | undefined
  selectedProfileId?: number | string
  isLoading: boolean
  onClose?: () => void
  handleMapDragEnd: (lat: number, lng: number) => void
  handleMapZoom?: (zoom: number) => void
  handleCardClick: (id?: string | number) => void
  setSelectedProfileId: (id?: string | number) => void
  onFiltersChange: (filters: SearchFiltersValues) => void
  onSearchAreaClick: () => void
  floating: ReturnType<typeof useFloatingProfile>
}

const MapFinderMobileContainer = styled(Box)(() => ({
  position: 'fixed',
  zIndex: 1199,
  width: '100%',
  height: '100%',
  background: 'white',
  top: 0,
  left: 0,
}))

const CardsWrapper = styled(Box)(() => ({
  borderTopLeftRadius: '18px',
  borderTopRightRadius: '18px',
  bottom: '0px',
  position: 'absolute',
  background: 'white',
  width: '100%',
  transition: 'linear 0.15s all',
  overflowY: 'hidden',
  zIndex: 11,
}))

const TouchBar = styled(Box)(() => ({
  position: 'relative',
  height: '90px',
  paddingBottom: '10px',
  '& .touch-bar': {
    position: 'absolute',
    left: '50%',
    top: '15px',
    transform: 'translateX(-50%)',
    width: '155px',
    height: '8px',
    backgroundColor: '#D2D1D4',
    borderRadius: '30px',
  },
}))

const CardsList = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: '10px',
  padding: '10px',
  marginBottom: '100px',
  position: 'relative',
  height: '38vh',
  minHeight: '300px',
  overflow: 'scroll',
}))

/**
 * ? 📺 MapDesktopFinder
 */
const MapMobile: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation('search')

  const {
    homeMarker,
    markers,
    coordinates,
    profiles,
    selectedProfile,
    selectedProfileId,
    onClose,
    handleMapDragEnd,
    setSelectedProfileId,
    onSearchAreaClick,
    handleMapZoom,
    isLoading,
    onFiltersChange,
    floating,
  } = props

  const [activeSearchButton, setActiveSearchButton] = useState(false)
  const [firstLoad, setFirstLoad] = useState(true)
  const [activeMobileCards, setActiveMobileCards] = useState<boolean>(false)

  // [HOOK] Filters
  const { filters, totalFilters, handleActiveFilter, FiltersComponent } = useFilters({
    activeLocalStorage: true,
    localStorageFiltersKey: 'filters_map',
    localStorageShortFiltersKey: 'shortFilter_map',
    filtersComponentProps: { variant: undefined, sx: { zIndex: 20, paddingLeft: '20px' } },
    disabledFilters: ['distance'],
  })

  // HOOK Swipe
  const { onTouchStart, onTouchEnd, onTouchMove } = useHorizontalSwipe((direction) => {
    const selectedCard = document.getElementById(`emtcard-selected-${selectedProfileId}`) as HTMLElement
    const nextProfileId =
      selectedCard.nextSibling && (selectedCard.nextSibling as HTMLElement).id.replace('emtcard-selected-', '')
    const prevProfileId =
      selectedCard.previousSibling && (selectedCard.previousSibling as HTMLElement).id.replace('emtcard-selected-', '')

    if (direction === 'left' && nextProfileId) handleCardClick(nextProfileId)
    if (direction === 'right' && prevProfileId) handleCardClick(prevProfileId)
  })

  const handleActiveMobileCards = (value: boolean) => {
    setActiveMobileCards(value)
  }

  const handleCardClick = (cardId: string | number) => {
    props.handleCardClick(cardId)
    setActiveMobileCards(false)
  }

  const onMapZoom = (zoom, coordinates: { lat: number; lng: number }) => {
    setActiveSearchButton(true)
    handleMapDragEnd(coordinates.lng, coordinates.lat)
    if (handleMapZoom) handleMapZoom(zoom)
  }

  useEffect(() => {
    if (!filters) return
    onFiltersChange(filters)
  }, [filters])

  useEffect(() => {
    setFirstLoad(false)
  }, [])

  useEffect(() => {
    if (firstLoad) return
    setActiveSearchButton(true)
  }, [coordinates])

  useEffect(() => {
    if (firstLoad) return
    !isLoading && setActiveSearchButton(false)
  }, [isLoading])

  return (
    <MapFinderMobileContainer>
      {/* CLOSE BUTTON */}
      <EmtFloatingIconButton
        className="EmtReactNativeWebViewMarginTop"
        iconClass="emt-arrow-left"
        onClick={onClose}
        sx={{ position: 'absolute', left: '10px', top: '15px', height: '44px', width: '44px' }}
      />
      {/* END CLOSE BUTTON */}

      {/* MAP */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          '& .img-marker': {
            maxWidth: '40px !important',
          },
        }}
      >
        <EmtMap
          zoom={16}
          homeMarker={homeMarker}
          markerList={markers}
          accessToken={Constants.mapboxAccessToken}
          lng={coordinates.lng}
          lat={coordinates.lat}
          onMapDragEnd={(lng, lat) => {
            setActiveSearchButton(true)
            handleMapDragEnd(lng, lat)
          }}
          onMapZoomEnd={onMapZoom}
          onMarkerMouseEnter={setSelectedProfileId}
          onMarkerMouseLeave={setSelectedProfileId}
          minZoom={4}
          maxZoom={17}
          navigationControl
          asyncRender
        />

        {activeSearchButton ? (
          <Box
            sx={{
              position: 'absolute',
              top: 75,
              zIndex: 11,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
            className="EmtReactNativeWebViewMarginTop"
          >
            <EmtButton
              sx={{
                width: '200px',
                padding: '6px',
                borderRadius: '12px',
                fontSize: '12px',
                '& .emt-icons': { width: 10 },
              }}
              startIcon={<EmtIcon className="emt-anti-clockwise-1" />}
              loader={isLoading}
              onClick={onSearchAreaClick}
            >
              {t('map.buttons.searchArea')}
            </EmtButton>
          </Box>
        ) : null}
      </Box>
      {/* END MAP */}

      {/* FILTER */}
      <Box
        sx={{ zIndex: 2, position: 'absolute', top: '15px', left: '50%', transform: 'translateX(-50%)' }}
        className="EmtReactNativeWebViewMarginTop"
      >
        <FiltersButton onClick={handleActiveFilter} count={filters ? totalFilters : undefined} />
      </Box>
      {FiltersComponent()}
      {/* END FILTER */}

      {/* Current selected profile */}
      <Box
        id="mapdrawer-current-cards-wrapper"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          position: 'absolute',
          bottom: '105px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          display: 'flex',
          zIndex: selectedProfile && !activeMobileCards ? 2 : -1000,
          flexDirection: 'row',
          gap: '10px',
          overflowX: 'scroll',
          padding: '0 10px',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        className="EmtReactNativeWebViewPaddingBottom"
      >
        {profiles.map((profile, k) => (
          <ProfileCardV2
            key={k}
            variant="inlineOutlined"
            fullCardLink={false}
            className={selectedProfileId === profile.id ? 'active' : ''}
            profile={profile}
            id={`emtcard-selected-${profile.id}`}
            sx={{
              minWidth: '320px',
              minHeight: '121px',
              '& .MuiAvatar-root': {
                minWidth: 90,
              },
              '& .EmtInlineCard-image-wrapper': {
                padding: '10px',
              },
            }}
            onClick={(e) => {
              e.stopPropagation()
              selectedProfileId === profile.id
                ? floating.handleOpenProfile(profile.id, ProfileKind.Community)
                : handleCardClick(profile.id)
            }}
            blankPage
          />
        ))}
      </Box>
      {/* END Current selected profile */}

      {/* CARDS */}
      <CardsWrapper
        id={'mobile-card-wrapper'}
        onTouchEnd={() => (!activeMobileCards && !isLoading ? handleActiveMobileCards(true) : null)}
        onClick={() => (!activeMobileCards && !isLoading ? handleActiveMobileCards(true) : null)}
        sx={{
          height: activeMobileCards ? '50vh' : '90px',
        }}
        className="EmtReactNativeWebViewPaddingBottom"
      >
        {/* CARDS HEADER */}
        <TouchBar
          onTouchEnd={() => (activeMobileCards ? handleActiveMobileCards(false) : null)}
          onClick={() => (activeMobileCards ? handleActiveMobileCards(false) : null)}
        >
          <Box className="touch-bar" />

          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '35px',
              }}
            >
              <Skeleton variant="text" height={24} width="60%" />
              <Skeleton variant="text" height={24} width="80%" />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: '35px',
              }}
            >
              <EmtTypography variant="h5" fontWeight={700}>
                {t(`map.totalfinding.title`, { count: profiles.length })}
              </EmtTypography>
              <EmtTypography variant="caption" fontWeight={600}>
                {t(`map.totalfinding.subtitle`)}
              </EmtTypography>
            </Box>
          )}
        </TouchBar>

        <CardsList
          id="mapdrawer-cards-wrapper"
          sx={{
            padding: activeMobileCards ? '10px' : '0px',
            height: activeMobileCards ? 'calc(100% - 90px)' : '0px',
          }}
        >
          {isLoading
            ? [...Array(6)].map((v, k) => (
                <div key={k} style={{ minWidth: '340px' }}>
                  <EmtCard variant="inline" loader />
                </div>
              ))
            : profiles.map((profile, k) => (
                <Box
                  key={k}
                  id={'mobile-wrapper'}
                  sx={{ minWidth: '340px' }}
                  onClick={(e) => {
                    const activeClick = (e.currentTarget.parentNode?.parentNode as HTMLElement).clientHeight > 200
                    selectedProfileId !== profile.id && activeClick ? handleCardClick(profile.id) : null
                  }}
                >
                  <ProfileCardV2
                    key={k}
                    variant="inlineOutlined"
                    className={selectedProfileId === profile.id ? 'active' : ''}
                    profile={profile}
                    id={`emtcard-${profile.id}`}
                    sx={{ cursor: 'pointer', '& .css-1je5pk2-MuiAvatar-root': { minWidth: '100px!important' } }}
                    onClick={() => {
                      handleCardClick(profile.id)
                    }}
                    onMobileClickCard={floating.handleOpenProfile}
                    blankPage
                  />
                </Box>
              ))}
        </CardsList>
      </CardsWrapper>
      {/* END CARDS */}

      {/* LOADER */}
      {isLoading ? (
        <>
          <Backdrop
            open={true}
            sx={{ zIndex: 1, background: (theme: any) => theme.palette.common.white, opacity: '0.6 !important' }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              top: '50%',
              width: '70%',
              zIndex: 11,
            }}
          >
            <LinearProgress color="primary" sx={{ height: 5 }} />
          </Box>
        </>
      ) : null}
      {/* END LOADER */}

      {/* FLOATING PROFILE */}
      <FloatingProfileModal
        isMobile={floating.isMobile}
        open={floating.profileIsOpen}
        customerId={floating.customerId}
        profileKind={floating.profileKind}
        onClose={floating.handleCloseProfile}
      />
      {/* END FLOATING PROFILE */}
    </MapFinderMobileContainer>
  )
}

export default MapMobile
